<template>
	<BaseModal :title="t('title')" v-model="model">
		<div class="px-5">
			<RouterLink
				to="/profile#fairness"
				v-if="!props.noRotate && fair.serverSeed === null"
				custom
				v-slot="{ navigate }"
			>
				<BaseCard
					class="flex items-center gap-4 p-5"
					alt
					v-hover="{ active: 'scale-95' }"
					@click="navigate()"
				>
					<div class="rounded-full bg-[#3F3F3F] p-2">
						<EyeStrikeIcon class="h-7 w-7 text-[#FFD326]" />
					</div>
					<div>
						<div class="text-label-primary text-sm font-medium capitalize">
							{{ t('serverSeedHidden') }}
						</div>
						<div class="text-label-secondary mt-1 text-sm">
							{{ t('rotateToReveal') }}
						</div>
					</div>
					<ChevronRightIcon
						class="text-label-tertiary h-5 w-5 shrink-0 opacity-30"
					/>
				</BaseCard>
			</RouterLink>
			<div
				class="bg-fill-secondary my-4 h-px w-full"
				v-if="!props.noRotate && fair.serverSeed === null"
			/>
			<FairCard @click="openFairInfo" />
			<BaseButton
				class="mt-4 w-full"
				size="lg"
				color="primary"
				@click="copy()"
				:loading="copying"
			>
				{{ t('copy') }}
			</BaseButton>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('data') }}
			</div>
			<BaseCard
				alt
				class="mt-1.5 overflow-x-scroll overflow-y-hidden px-3 py-2 !select-text"
				:class="{ 'max-h-36': !isExpanded }"
			>
				<pre><code v-html="JSON.stringify({ ...fair, uuid: user.uuid }, null, 2)" /></pre>
			</BaseCard>
			<div
				class="text-label-tertiary mt-4 flex w-fit items-center gap-1.5 text-sm font-medium uppercase"
				v-hover="{ active: 'scale-95', impact: 'soft' }"
				@click="toggleExpand()"
			>
				<CaretRightIcon
					class="h-5 w-5 transition-transform"
					:class="{ 'rotate-90': !isExpanded, 'rotate-270': isExpanded }"
				/>
				<div>{{ expandText }}</div>
			</div>
			<div class="bg-fill-secondary mt-4 h-px w-full" />
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('nonce') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.nonce.join(', ') }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('userUuid') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ user.uuid }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('serverSeed') }}
			</div>
			<BaseCard
				alt
				class="mt-1.5 px-3 py-2 break-words !select-text"
				:class="{
					'flex items-center gap-4': !props.noRotate && fair.serverSeed === null
				}"
			>
				<code>{{ fair.serverSeed ? fair.serverSeed : t('hidden') }}</code>
				<RouterLink
					to="/profile#fairness"
					v-if="!props.noRotate && fair.serverSeed === null"
					custom
					v-slot="{ navigate }"
				>
					<div
						class="text-label-tertiary ml-auto inline-flex items-center gap-1.5 text-sm font-medium uppercase"
						v-hover="{ active: 'scale-95', impact: 'soft' }"
						@click="navigate()"
					>
						<div>{{ t('rotate') }}</div>
						<CaretRightIcon class="h-6 w-6" />
					</div>
				</RouterLink>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('serverSeedHash') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.serverSeedHash }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('clientSeed') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.clientSeed }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('tickets') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.ticketsCount }}</code>
			</BaseCard>
			<div class="text-label-secondary mt-5 text-xs font-medium uppercase">
				{{ t('ticket') }}
			</div>
			<BaseCard alt class="mt-1.5 px-3 py-2 break-words !select-text">
				<code>{{ fair.ticket ? fair.ticket.join(', ') : t('waiting') }}</code>
			</BaseCard>
		</div>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import FairCard from '../FairCard.vue'
import BaseCard from '../BaseCard.vue'
import { useToast } from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import CaretRightIcon from '@/assets/icons/caret-right.svg'
import EyeStrikeIcon from '@/assets/icons/eye-strike.svg'
import ChevronRightIcon from '@/assets/icons/chevron-right.svg'
import { useUser } from '@/stores/user'
import { useLayout } from '@/stores/layout'

const { t } = useI18n({
	messages: {
		en: {
			title: 'fairness',
			copied: 'Copied to clipboard',
			copy: 'Copy data',
			data: 'Case data',
			serverSeed: 'Server Seed',
			serverSeedHash: 'Server Seed Hash',
			clientSeed: 'Client Seed',
			tickets: 'Total Tickets',
			ticket: 'Ticket',
			hidden: 'HIDDEN',
			waiting: 'Waiting for open...',
			rotate: 'Rotate',
			serverSeedHidden: 'Server Seed is Hidden',
			rotateToReveal: 'Rotate server seed to verify this unboxing',
			more: 'show more',
			less: 'show less',
			nonce: 'nonce',
			userUuid: 'user uuid'
		},
		ru: {
			title: 'честность',
			copied: 'Скопировано',
			copy: 'Копировать данные',
			data: 'Данные открытия',
			serverSeed: 'Сервер-seed',
			serverSeedHash: 'Хеш сервер-seed',
			clientSeed: 'Клиент-seed',
			tickets: 'Всего билетов',
			ticket: 'Билет',
			hidden: 'СКРЫТ',
			waiting: 'Ожидание открытия...',
			rotate: 'Поменять',
			serverSeedHidden: 'Сервер-seed скрыт',
			rotateToReveal: 'Поменяйте сервер-seed, чтобы проверить это открытие',
			more: 'больше',
			less: 'меньше',
			nonce: 'nonce',
			userUuid: 'uuid пользователя'
		}
	}
})

const props = defineProps<{
	fair: {
		nonce: number[]
		clientSeed: string
		serverSeed: string | null
		serverSeedHash: string
		ticketsCount: number
		ticket: number[] | null
	}
	noRotate?: boolean
}>()

const model = defineModel<boolean>()

const isExpanded = ref(false)

const toggleExpand = () => {
	isExpanded.value = !isExpanded.value
}

const expandText = computed(() => {
	return isExpanded.value ? t('less') : t('more')
})

const copying = ref(false)

const toast = useToast()
const user = useUser()

const copy = async () => {
	copying.value = true

	try {
		await navigator.clipboard.writeText(
			JSON.stringify({ ...props.fair, uuid: user.uuid }, null, 2)
		)

		toast.add({
			type: 'success',
			text: t('copied')
		})
	} catch {}

	copying.value = false
}

const layout = useLayout()

const openFairInfo = () => {
	model.value = false
	layout.openFairInfoModal(1)
}
</script>
